// Shared Section-Rendering-API helpers for the byRavn cart mutations.
//
// Extracted from byRavn-gavepose.js once byRavn-cart-preview-strip.js needed the same
// painting semantics. paintAllSurfaces in particular carries non-obvious behaviour that
// must not be duplicated: the union of every surface's sections, the re-queried
// sourceElement, and the cart:refresh re-dispatch. See the comments below.
//
// Must load before byRavn-gavepose.js and byRavn-cart-preview-strip.js (all deferred, so
// document order in theme.liquid is the ordering).
(function () {
  function cartSurfaces() {
    return Array.from(document.querySelectorAll('cart-items, cart-drawer-items'));
  }

  // Union of every present surface's sections: on /cart both the page and the
  // always-rendered drawer exist, and painting only the mutating surface leaves the
  // other one's totals stale (main-cart-footer is not covered by onCartUpdate).
  // /cart yields exactly 5 sections — Shopify's bundled section rendering maximum.
  function sectionsPayload() {
    const sections = new Set();
    cartSurfaces().forEach((surface) => {
      surface.getSectionsToRender().forEach((section) => sections.add(section.section));
    });
    return { sections: Array.from(sections), sections_url: window.location.pathname };
  }

  // `source` is the publish() label the caller wants on the cartUpdate event, so each
  // mutation stays identifiable to subscribers (byRavn-cart-preview-strip.js skips its own).
  function paintAllSurfaces(state, sourceSurface, source) {
    const sourceTag = sourceSurface.tagName;
    cartSurfaces().forEach((surface) => {
      surface.getSectionsToRender().forEach((section) => {
        const html = state.sections[section.section];
        const container = document.getElementById(section.id);
        if (!html || !container) return;
        const target = container.querySelector(section.selector) || container;
        target.innerHTML = surface.getSectionInnerHTML(html, section.selector);
      });
    });
    // Painting the drawer replaces the <cart-drawer-items> element itself (it lives
    // inside .drawer__inner), so `sourceSurface` is now detached. Publish with the
    // FRESH instance so its own cartUpdate subscriber (cart.js) matches the
    // sourceElement guard and skips a redundant self-refetch that would otherwise
    // destroy the just-restored focus. Other subscribers (messaging-bar hydrate,
    // price-per-item) still run.
    const freshSource = document.querySelector(sourceTag) || sourceSurface;
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: source || 'byRavn-cart',
      sourceElement: freshSource,
      cartData: state,
    });
    // The legal-accept checkout gate (cart.js bindCartLegal) only re-binds on these
    // DOM events, and our repaint replaced #CartLegalAgree / #CartDrawer-Checkout —
    // re-fire one or the checkout button stays unbound after a mutation.
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  function fetchCartSections() {
    const url = `${window.routes.cart_url}?sections=${encodeURIComponent(sectionsPayload().sections.join(','))}`;
    return fetch(url)
      .then((response) => response.json())
      .then((sections) => ({ sections }));
  }

  // Block the quantity/remove controls while a mutation is in flight, like Dawn's
  // enableLoading does — otherwise trashing a line mid-write races the cascade.
  // Depth-counted, not a boolean: two holders can overlap (the gavepose toggle parked
  // on zeptoSettled while the gravering reconciler runs a pass), and a plain toggle
  // would let whichever finishes first unlock the surfaces under the other. Callers
  // must stay balanced — every setBusy(true) needs exactly one setBusy(false).
  let busyDepth = 0;
  function setBusy(busy) {
    busyDepth = Math.max(0, busyDepth + (busy ? 1 : -1));
    ['main-cart-items', 'CartDrawer-CartItems'].forEach((id) => {
      const container = document.getElementById(id);
      if (container) container.classList.toggle('cart__items--disabled', busyDepth > 0);
    });
  }

  // Zepto keeps its own snapshot of a line's property set in `_pplr_attribute_data` and
  // REPLACES the line's properties from it when the order is created (verified against
  // abandoned checkout #68070617350475 vs order #1011) — a property edit that skips the
  // snapshot is silently undone on the order. `mutate` receives the parsed snapshot
  // object and edits it in place; return false to leave the stored snapshot byte-for-byte
  // untouched (a rewrite of an unchanged snapshot would still re-key the line). Lines
  // Zepto does not manage have no snapshot and the whole call is a no-op.
  function patchZeptoSnapshot(properties, mutate) {
    const key = Object.keys(properties).find((name) => name.replace(/^_+/, '') === 'pplr_attribute_data');
    if (!key) return;
    let snapshot;
    try {
      snapshot = JSON.parse(properties[key]);
    } catch (error) {
      return;
    }
    if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) return;
    if (mutate(snapshot) === false) return;
    properties[key] = JSON.stringify(snapshot);
  }

  window.byRavnCart = {
    surfaces: cartSurfaces,
    sectionsPayload,
    paintAllSurfaces,
    fetchCartSections,
    setBusy,
    patchZeptoSnapshot,
  };
})();
